import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Tooltip,
  Alert,
  Spin,
  Empty,
  App,
  Dropdown,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ScanOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useGetClientesFiscalesQuery,
  useCreateClienteFiscalMutation,
  useUpdateClienteFiscalMutation,
  useDeleteClienteFiscalMutation,
  useRefrescarClientesFiscalesMutation,
  useGetUsosCFDIQuery,
  useGetRegimenesFiscalesQuery,
  useImportClientesFiscalesMutation,
} from '../../services/facturacionApi';
import type { ClienteFiscal, ParsedClienteDataDto } from '../../types/facturacion';
import { useResponsive } from '../../hooks/useResponsive';
import CsvImportModal from '../common/CsvImportModal';
import type { ColumnConfig } from '../../types/import';
import { useQRScanner } from '../../hooks/useQRScanner';
import QRScannerModal from './QRScannerModal';
import QRDataPreviewModal from './QRDataPreviewModal';

const { Title, Text } = Typography;

interface ClientesFiscalesManagerProps {
  /** Contador que la barra de la página incrementa para abrir el modal de alta */
  addSignal?: number;
}

const ClientesFiscalesManager: React.FC<ClientesFiscalesManagerProps> = ({ addSignal }) => {
  const { message: messageApi } = App.useApp();
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<ClienteFiscal | null>(null);
  const [clienteToDelete, setClienteToDelete] = useState<{ facturadorClienteId: number; rfc: string; razonSocial: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Dirección completa colapsada por defecto; se expande al editar un cliente que ya la tiene
  const [showDireccion, setShowDireccion] = useState(false);

  // QR Scanner states
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showQRPreview, setShowQRPreview] = useState(false);
  const qrScanner = useQRScanner();

  // Flag para prevenir re-aperturas (persiste entre renders)
  const modalWasClosedRef = useRef(false);

  // API Queries
  const { data: clientes = [], isLoading: loadingClientes } = useGetClientesFiscalesQuery();
  const { data: usosCFDI = [] } = useGetUsosCFDIQuery();
  const { data: regimenesFiscales = [] } = useGetRegimenesFiscalesQuery();

  // Mutations
  const [createCliente, { isLoading: creating }] = useCreateClienteFiscalMutation();
  const [updateCliente, { isLoading: updating }] = useUpdateClienteFiscalMutation();
  const [deleteCliente, { isLoading: deleting }] = useDeleteClienteFiscalMutation();
  const [refrescarClientes, { isLoading: refreshing }] = useRefrescarClientesFiscalesMutation();
  const [importClientesFiscales] = useImportClientesFiscalesMutation();

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);

  // Filter clientes by search term
  const filteredClientes = clientes.filter(
    (c) =>
      c.rfc.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.razonSocial.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.nombreComercial && c.nombreComercial.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleOpenModal = (cliente?: ClienteFiscal) => {
    setShowDireccion(
      !!(cliente && (cliente.calle || cliente.numeroExterior || cliente.numeroInterior ||
        cliente.colonia || cliente.municipio || cliente.estado))
    );
    if (cliente) {
      setSelectedCliente(cliente);
      form.setFieldsValue({
        rfc: cliente.rfc,
        razonSocial: cliente.razonSocial,
        nombreComercial: cliente.nombreComercial,
        usoCFDI: cliente.usoCFDI,
        regimenFiscal: cliente.regimenFiscal,
        email: cliente.email,
        telefono: cliente.telefono,
        codigoPostal: cliente.codigoPostal,
        calle: cliente.calle,
        numeroExterior: cliente.numeroExterior,
        numeroInterior: cliente.numeroInterior,
        colonia: cliente.colonia,
        municipio: cliente.municipio,
        estado: cliente.estado,
        pais: cliente.pais || 'México',
      });
    } else {
      setSelectedCliente(null);
      form.resetFields();
      form.setFieldsValue({ pais: 'México' });
    }
    setShowModal(true);
  };

  // La barra de la página incrementa addSignal para abrir el alta desde "Agregar cliente"
  useEffect(() => {
    if (addSignal && addSignal > 0) {
      handleOpenModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSignal]);

  const handleSaveCliente = async () => {
    try {
      const values = await form.validateFields();

      if (selectedCliente) {
        // Update existing cliente
        if (!selectedCliente.facturadorClienteId) {
          messageApi.error('El cliente no tiene un ID válido del Facturador');
          return;
        }
        await updateCliente({
          id: selectedCliente.facturadorClienteId.toString(),
          ...values,
        }).unwrap();
        messageApi.success('Cliente actualizado correctamente', 3);
      } else {
        // Create new cliente
        await createCliente(values).unwrap();
        messageApi.success('Cliente creado correctamente', 3);
      }

      setShowModal(false);
      form.resetFields();
      setSelectedCliente(null);
    } catch (error: any) {
      console.error('Error saving cliente:', error);
      messageApi.error(error?.data?.message || 'Error al guardar el cliente', 5);
    }
  };

  const handleDeleteCliente = (facturadorClienteId: number | undefined, rfc: string, razonSocial: string) => {
    if (!facturadorClienteId) {
      messageApi.error('El cliente no tiene un ID válido del Facturador');
      return;
    }
    setClienteToDelete({ facturadorClienteId, rfc, razonSocial });
    setShowDeleteModal(true);
  };

  const confirmDeleteCliente = async () => {
    if (!clienteToDelete) return;

    try {
      await deleteCliente(clienteToDelete.facturadorClienteId.toString()).unwrap();
      messageApi.success('Cliente eliminado correctamente', 3);
      setShowDeleteModal(false);
      setClienteToDelete(null);
    } catch (error: any) {
      console.error('Error deleting cliente:', error);
      messageApi.error(error?.data?.message || 'Error al eliminar el cliente', 5);
    }
  };

  const handleRefreshClientes = async () => {
    try {
      const result = await refrescarClientes().unwrap();
      messageApi.success(`Clientes actualizados: ${result.count} cliente(s) en caché`);
    } catch (error: any) {
      const errorMsg = error.data?.message || error.data?.title || 'Error al refrescar clientes';
      messageApi.error(errorMsg);
    }
  };

  // QR Scanner handlers
  const handleOpenQRScanner = () => {
    setShowQRScanner(true);
    qrScanner.resetScanner();
    modalWasClosedRef.current = false; // Reset flag cuando se abre nuevo escaneo
  };

  const handleQRDetected = async (qrContent: string) => {
    setShowQRScanner(false);

    const parsedData = await qrScanner.handleScan(qrContent);

    if (parsedData && !modalWasClosedRef.current) {
      setShowQRPreview(true);
    } else if (qrScanner.error) {
      messageApi.error(qrScanner.error, 5);
    }
  };

  const handleConfirmQRData = (data: ParsedClienteDataDto) => {
    // Marcar que el modal fue cerrado manualmente PRIMERO
    modalWasClosedRef.current = true;

    // Limpiar estados INMEDIATAMENTE (sin setTimeout)
    setShowQRPreview(false);
    qrScanner.resetScanner();

    // Preparar datos del formulario
    setSelectedCliente(null);
    setShowDireccion(false);
    form.resetFields();
    form.setFieldsValue({
      rfc: data.rfc,
      razonSocial: data.razonSocial,
      regimenFiscal: data.regimenFiscal,
      codigoPostal: data.codigoPostal,
      pais: 'México',
    });

    // Abrir modal de crear cliente con un pequeño delay para que se cierre el anterior
    setTimeout(() => {
      setShowModal(true);
      messageApi.success('Datos del QR cargados. Complete los campos adicionales.');
    }, 200);
  };

  const handleEditManuallyFromQR = () => {
    modalWasClosedRef.current = true;
    setShowQRPreview(false);
    qrScanner.resetScanner();

    setTimeout(() => {
      handleOpenModal();
    }, 300);
  };

  // Clave SAT + descripción visible debajo — sin ir a cazar tooltips
  const claveConDescripcion = (clave: string, descripcion?: string) => (
    <div>
      <span style={{ fontWeight: 500 }}>{clave}</span>
      {descripcion && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{descripcion}</div>
      )}
    </div>
  );

  const columns: ColumnsType<ClienteFiscal> = [
    {
      title: 'RFC',
      dataIndex: 'rfc',
      key: 'rfc',
      width: 170,
      sorter: (a, b) => a.rfc.localeCompare(b.rfc),
      render: (rfc) => (
        <span className="fac-mono" style={{ fontWeight: 600, fontSize: 13 }}>
          {rfc}
          {rfc === 'XAXX010101000' && (
            <span className="fac-estado" style={{ marginLeft: 6 }}>público gral.</span>
          )}
        </span>
      ),
    },
    {
      title: 'Razón social',
      dataIndex: 'razonSocial',
      key: 'razonSocial',
      sorter: (a, b) => a.razonSocial.localeCompare(b.razonSocial),
      render: (razonSocial, record) => (
        <div>
          <Text strong>{razonSocial}</Text>
          {record.nombreComercial && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.nombreComercial}
              </Text>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Uso CFDI',
      dataIndex: 'usoCFDI',
      key: 'usoCFDI',
      width: 190,
      render: (usoCFDI) => {
        const uso = usosCFDI.find((u) => u.clave === usoCFDI);
        return claveConDescripcion(usoCFDI, uso?.descripcion);
      },
    },
    {
      title: 'Régimen fiscal',
      dataIndex: 'regimenFiscal',
      key: 'regimenFiscal',
      width: 190,
      render: (regimenFiscal) => {
        const regimen = regimenesFiscales.find((r) => r.clave === regimenFiscal);
        return claveConDescripcion(regimenFiscal, regimen?.descripcion);
      },
    },
    {
      title: 'Contacto',
      key: 'contacto',
      width: 190,
      render: (_, record) => (
        <div>
          {record.email && (
            <div>
              <Text style={{ fontSize: 12 }}>{record.email}</Text>
            </div>
          )}
          {record.telefono && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.telefono}
              </Text>
            </div>
          )}
          {!record.email && !record.telefono && <Text type="secondary">—</Text>}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      fixed: 'right',
      align: 'right',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Editar cliente">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Tooltip title="Eliminar cliente">
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCliente(record.facturadorClienteId, record.rfc, record.razonSocial)}
              loading={deleting}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const loading = loadingClientes;

  // Mobile card renderer
  const renderMobileCards = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {filteredClientes.map((cliente) => {
        const uso = usosCFDI.find((u) => u.clave === cliente.usoCFDI);
        const regimen = regimenesFiscales.find((r) => r.clave === cliente.regimenFiscal);

        return (
          <Card
            key={cliente.id}
            size="small"
            style={{ borderRadius: 8 }}
            bodyStyle={{ padding: 12 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fac-mono" style={{ fontWeight: 600, fontSize: 12, marginBottom: 2 }}>
                  {cliente.rfc}
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {cliente.razonSocial}
                </div>
                {cliente.nombreComercial && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {cliente.nombreComercial}
                  </Text>
                )}
              </div>
              <Space size={4}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenModal(cliente)}
                />
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteCliente(cliente.facturadorClienteId, cliente.rfc, cliente.razonSocial)}
                />
              </Space>
            </div>

            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              <div>{cliente.usoCFDI}{uso?.descripcion ? ` · ${uso.descripcion}` : ''}</div>
              <div>{cliente.regimenFiscal}{regimen?.descripcion ? ` · ${regimen.descripcion}` : ''}</div>
            </div>

            {(cliente.email || cliente.telefono) && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
                {cliente.email && <div>{cliente.email}</div>}
                {cliente.telefono && <div>{cliente.telefono}</div>}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Cargando clientes fiscales...</Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* Header consolidado: búsqueda + conteo + importar (CSV/QR agrupados) + refrescar.
          "Agregar cliente" vive en la barra de la página. */}
      <div className="fac-filters">
        <Input
          placeholder={isMobile ? 'Buscar…' : 'RFC, razón social o nombre comercial'}
          prefix={<SearchOutlined />}
          style={{ width: isMobile ? '100%' : 280 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />
        <Text type="secondary" style={{ fontSize: 13 }}>
          {filteredClientes.length} cliente{filteredClientes.length !== 1 ? 's' : ''}
        </Text>
        <div style={{ flex: 1 }} />
        <Dropdown
          menu={{
            items: [
              {
                key: 'csv',
                icon: <UploadOutlined />,
                label: 'Archivo CSV',
                onClick: () => setShowImportModal(true),
              },
              {
                key: 'qr',
                icon: <ScanOutlined />,
                label: 'QR de constancia fiscal',
                onClick: handleOpenQRScanner,
              },
            ],
          }}
        >
          <Button icon={<UploadOutlined />}>Importar</Button>
        </Dropdown>
        <Tooltip title="Refrescar desde Facturador">
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={handleRefreshClientes}
            loading={refreshing}
          />
        </Tooltip>
      </div>

      {filteredClientes.length === 0 ? (
        <div className="fac-panel" style={{ padding: 24 }}>
          <Empty
            description={searchTerm ? 'No se encontraron clientes' : 'No hay clientes fiscales registrados'}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {!searchTerm && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                Agregar primer cliente
              </Button>
            )}
          </Empty>
        </div>
      ) : isMobile ? (
        renderMobileCards()
      ) : (
        <div className="fac-panel">
          <Table
            columns={columns}
            dataSource={filteredClientes}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} clientes`,
            }}
            scroll={{ x: 1100 }}
          />
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 28 }}>
            <span>{selectedCliente ? 'Editar cliente' : 'Nuevo cliente'}</span>
            {!selectedCliente && (
              <Button size="small" icon={<ScanOutlined />} onClick={handleOpenQRScanner}>
                {!isMobile && 'Escanear constancia fiscal'}
              </Button>
            )}
          </div>
        }
        open={showModal}
        onOk={handleSaveCliente}
        onCancel={() => {
          setShowModal(false);
          form.resetFields();
          setSelectedCliente(null);
        }}
        confirmLoading={creating || updating}
        okText="Guardar cliente"
        cancelText="Cancelar"
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto', padding: '12px' } : {}}
      >
        <Form form={form} layout="vertical">
          <Row gutter={isMobile ? 8 : 16}>
            <Col xs={24} sm={9}>
              <Form.Item
                name="rfc"
                label="RFC"
                hasFeedback
                normalize={(value) => value?.toUpperCase()}
                rules={[
                  { required: true, message: 'Ingrese el RFC del cliente' },
                  {
                    pattern: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/,
                    message: 'RFC inválido (ej: XAXX010101000)',
                  },
                ]}
              >
                <Input
                  placeholder="XAXX010101000"
                  maxLength={13}
                  style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={15}>
              <Form.Item
                name="razonSocial"
                label="Razón social"
                rules={[{ required: true, message: 'Ingrese la razón social' }]}
              >
                <Input placeholder="Razón social del cliente" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="nombreComercial" label="Nombre comercial (opcional)">
            <Input placeholder="Nombre comercial" />
          </Form.Item>

          <Row gutter={isMobile ? 8 : 16}>
            <Col xs={24} sm={9}>
              <Form.Item
                name="usoCFDI"
                label="Uso CFDI"
                rules={[{ required: true, message: 'Seleccione uso CFDI' }]}
              >
                <Select
                  placeholder="Seleccione uso CFDI"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {usosCFDI.map((uso) => (
                    <Select.Option key={uso.clave} value={uso.clave}>
                      {isMobile ? uso.clave : `${uso.clave} - ${uso.descripcion}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={9}>
              <Form.Item
                name="regimenFiscal"
                label="Régimen fiscal"
                rules={[{ required: true, message: 'Seleccione régimen' }]}
              >
                <Select
                  placeholder="Seleccione régimen"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {regimenesFiscales.map((regimen) => (
                    <Select.Option key={regimen.clave} value={regimen.clave}>
                      {isMobile ? regimen.clave : `${regimen.clave} - ${regimen.descripcion}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              {/* CFDI 4.0 exige el DomicilioFiscalReceptor (C.P.) para timbrar */}
              <Form.Item
                name="codigoPostal"
                label="C.P. fiscal"
                rules={[
                  { required: true, message: 'C.P. requerido' },
                  { pattern: /^\d{5}$/, message: '5 dígitos' },
                ]}
              >
                <Input placeholder="01000" maxLength={5} />
              </Form.Item>
            </Col>
          </Row>

          {/* Datos de Contacto */}
          <Title level={5} style={{ marginTop: isMobile ? 8 : 12 }}>
            Contacto <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>· para enviarle sus facturas</Text>
          </Title>

          <Row gutter={isMobile ? 8 : 16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: 'email', message: 'Email inválido' }]}
              >
                <Input placeholder="correo@ejemplo.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="telefono" label="Teléfono">
                <Input placeholder="5512345678" />
              </Form.Item>
            </Col>
          </Row>

          {/* Dirección completa colapsada: solo el C.P. pesa para el CFDI */}
          {!showDireccion ? (
            <div style={{ borderTop: '1px dashed var(--border-color)', paddingTop: 10, marginTop: 4 }}>
              <Button type="link" style={{ paddingLeft: 0 }} onClick={() => setShowDireccion(true)}>
                + Agregar dirección completa
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                (opcional — calle, colonia, municipio…)
              </Text>
            </div>
          ) : (
            <>
              <Title level={5} style={{ marginTop: isMobile ? 8 : 12 }}>
                Dirección <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>· opcional</Text>
              </Title>

              <Row gutter={isMobile ? 8 : 16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="calle" label="Calle">
                    <Input placeholder="Calle" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="numeroExterior" label="No. Ext">
                    <Input placeholder="123" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="numeroInterior" label="No. Int">
                    <Input placeholder="A" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={isMobile ? 8 : 16}>
                <Col xs={24} sm={8}>
                  <Form.Item name="colonia" label="Colonia">
                    <Input placeholder="Colonia" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="municipio" label="Municipio">
                    <Input placeholder="Municipio" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={5}>
                  <Form.Item name="estado" label="Estado">
                    <Input placeholder="Estado" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={5}>
                  <Form.Item name="pais" label="País">
                    <Input placeholder="México" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="¿Eliminar cliente?"
        open={showDeleteModal}
        onOk={confirmDeleteCliente}
        onCancel={() => {
          setShowDeleteModal(false);
          setClienteToDelete(null);
        }}
        confirmLoading={deleting}
        okText="Eliminar"
        okType="danger"
        cancelText="Cancelar"
        width={isMobile ? '90%' : 500}
      >
        <Alert
          message={isMobile ? "Confirmar eliminación" : "Confirmación de Eliminación"}
          description={
            <div>
              <Text>
                Se eliminará: <strong>{clienteToDelete?.rfc}</strong>
                {!isMobile && <> - <strong>{clienteToDelete?.razonSocial}</strong></>}
              </Text>
              {isMobile && clienteToDelete?.razonSocial && (
                <div><strong>{clienteToDelete.razonSocial}</strong></div>
              )}
              <br />
              <Text type="danger">Esta acción no se puede deshacer.</Text>
            </div>
          }
          type="warning"
          showIcon
          icon={<DeleteOutlined />}
        />
      </Modal>

      {/* ===== CSV Import Modal ===== */}
      <CsvImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Clientes Fiscales"
        templateEndpoint="/facturacion/clientes/import/template"
        templateFileName="plantilla_clientes_fiscales.csv"
        expectedColumns={[
          { name: 'RFC', label: 'RFC', required: true, type: 'string', validate: (v) => /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(v.toUpperCase()) ? null : 'RFC invalido' },
          { name: 'RazonSocial', label: 'Razon Social', required: true, type: 'string' },
          { name: 'NombreComercial', label: 'Nombre Comercial', required: false, type: 'string' },
          { name: 'UsoCFDI', label: 'Uso CFDI', required: true, type: 'string' },
          { name: 'RegimenFiscal', label: 'Regimen Fiscal', required: true, type: 'string' },
          { name: 'Email', label: 'Email', required: false, type: 'string' },
          { name: 'Telefono', label: 'Telefono', required: false, type: 'string' },
          { name: 'CodigoPostal', label: 'Codigo Postal', required: false, type: 'string' },
          { name: 'Calle', label: 'Calle', required: false, type: 'string' },
          { name: 'NumeroExterior', label: 'No. Exterior', required: false, type: 'string' },
          { name: 'NumeroInterior', label: 'No. Interior', required: false, type: 'string' },
          { name: 'Colonia', label: 'Colonia', required: false, type: 'string' },
          { name: 'Municipio', label: 'Municipio', required: false, type: 'string' },
          { name: 'Estado', label: 'Estado', required: false, type: 'string' },
        ] as ColumnConfig[]}
        onImport={async (formData) => {
          const result = await importClientesFiscales(formData).unwrap();
          return result;
        }}
      />

      {/* QR Scanner Modal */}
      <QRScannerModal
        visible={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onQRDetected={handleQRDetected}
        onManualEntry={handleEditManuallyFromQR}
      />

      {/* QR Data Preview Modal */}
      {showQRPreview && qrScanner.parsedData && !modalWasClosedRef.current && (
        <QRDataPreviewModal
          visible={showQRPreview}
          parsedData={qrScanner.parsedData}
          onConfirm={handleConfirmQRData}
          onEdit={handleEditManuallyFromQR}
          onCancel={() => {
            modalWasClosedRef.current = true;
            setShowQRPreview(false);
            qrScanner.resetScanner();
          }}
        />
      )}
    </div>
  );
};

export default ClientesFiscalesManager;
